<script setup lang="ts">
import type { MenuOption } from 'naive-ui'
import { requestDeleteFeed } from '~/apis/feed/requestDeleteFeed'

const props = defineProps<{
  feed: Feed.Info
}>()

const emit = defineEmits(['delete'])

const $toast = useToast()
const $confirm = useConfirm()

const { data: userInfo } = useAuth()

const isSelf = computed(() => props.feed.user.id === userInfo.value?.user.id)

function getAction(
  isAutoSync: boolean,
  resourceType: ResourceType,
  extraType: number
) {
  if (isAutoSync) {
    switch (resourceType) {
      case 0:
        return '发布了一篇文章'
      case 1:
        return '发布了一个视频'
      default:
        break
    }
  } else {
    switch (extraType) {
      case 0:
        return '发布了新动态'
      case 1:
      case 2:
      case 3:
        return '转发了'
      default:
        break
    }
  }
}

const action = computed(() => {
  return getAction(
    props.feed.is_auto_sync,
    props.feed.resource_type,
    props.feed.extra_type
  )
})

const options: MenuOption[] = [
  {
    label: '删除动态',
    key: 'delete'
  }
]

async function handleSelect(key: string) {
  let res: any = {}
  switch (key) {
    case 'delete':
      $confirm('确认删除此动态？', '删除后无法恢复', {
        type: 'error',
        confirmButtonText: '删除',
        onConfirm: async () => {
          res = await requestDeleteFeed(props.feed.id)
          if (res.code === 1) {
            $toast.success('已删除该动态')
            emit('delete')
          } else {
            $toast.error(res.msg)
          }
        }
      })
      break
    default:
      break
  }
}
</script>

<template>
  <div class="m-feed-card">
    <div class="m-feed-card__header">
      <m-user-info
        :id="feed.user.id"
        :avatar="feed.user.avatar"
        :badges="feed.user.badges"
        :name="feed.user.name"
        :auth="feed.user.user_auth"
        link
      >
        <div>{{ pipe.date(feed.created_at) }} {{ action }}</div>
      </m-user-info>

      <n-dropdown
        v-if="isSelf"
        trigger="click"
        :options="options"
        @select="handleSelect"
      >
        <n-button text round class="more-button">
          <m-icon icon="lucide-more-vertical" />
        </n-button>
      </n-dropdown>
    </div>
    <div class="m-feed-card__content-wrapper">
      <m-link
        :key="feed.id"
        :resource-id="feed.resource_id"
        :resource-type="feed.resource_type"
        :title="feed.title"
        class="m-feed-card__body"
      >
        <h3 v-if="feed.title" class="m-feed-card__h3">{{ feed.title }}</h3>
        <div
          class="m-feed-card__content"
          v-html="xss(setTargetBlank(feed.content))"
        ></div>
      </m-link>
      <div v-if="feed.extra.images?.length" class="m-feed-card__gallery">
        <m-image-preview>
          <m-gallery :list="feed.extra.images"></m-gallery>
        </m-image-preview>
      </div>
      <div
        v-if="[1, 2, 3].includes(feed.extra_type)"
        class="m-feed-card__forward"
      >
        <feed-forward-feed
          v-if="feed.extra_type === 1"
          :id="feed.extra.resource.id"
          :title="feed.extra.resource.title"
          :content="feed.extra.resource.content"
          :images="feed.extra.resource.extra.images"
          :cover="feed.extra.resource.cover"
          :tags="feed.extra.resource.tag"
          :likes="feed.extra.resource.like_status?.like?.count"
          :comments="feed.extra.resource.floor_count"
          :views="feed.extra.resource.views"
          :user-id="feed.extra.user.id"
          :user-avatar="feed.extra.user.avatar"
          :badges="feed.extra.user.badges"
          :username="feed.extra.user.name"
          :user-auth="feed.extra.user.user_auth"
          :created-at="feed.extra.resource.created_at"
        />
        <feed-forward-article
          v-else-if="feed.extra_type === 2"
          :id="feed.extra.resource.id"
          :title="feed.extra.resource.title"
          :summary="feed.extra.resource.summary"
          :cover="feed.extra.resource.cover"
          :tags="feed.extra.resource.tag"
          :likes="feed.extra.resource.like_count"
          :comments="feed.extra.resource.comment_count"
          :views="feed.extra.resource.view_count"
          :user-id="feed.extra.user.id"
          :user-avatar="feed.extra.user.avatar"
          :badges="feed.extra.user.badges"
          :username="feed.extra.user.name"
          :user-auth="feed.extra.user.user_auth"
          :created-at="feed.extra.resource.created_at"
        />
        <feed-forward-video
          v-else-if="feed.extra_type === 3"
          :id="feed.extra.resource.id"
          :title="feed.extra.resource.title"
          :summary="feed.extra.resource.summary"
          :duration="feed.extra.resource.duration"
          :cover="feed.extra.resource.cover"
          :tags="feed.extra.resource.tag"
          :likes="feed.extra.resource.like_count"
          :comments="feed.extra.resource.comment_count"
          :views="feed.extra.resource.view_count"
          :user-id="feed.extra.user.id"
          :user-avatar="feed.extra.user.avatar"
          :badges="feed.extra.user.badges"
          :username="feed.extra.user.name"
          :user-auth="feed.extra.user.user_auth"
          :created-at="feed.extra.resource.created_at"
        />
      </div>
      <div v-if="feed.tags.length" class="m-feed-card__tags">
        <m-tag v-for="tag in feed.tags" :key="tag" link :label="tag" />
      </div>
    </div>
    <div class="m-feed-card__action">
      <div class="m-feed-card__action-item">
        <m-like-action
          :resource-id="feed.resource_id"
          :resource-type="feed.resource_type"
          :like-status="feed.like_status"
          type="like"
        >
          <span class="text">
            {{ pipe.num(feed.like_status.like.count) || '喜欢' }}
          </span>
        </m-like-action>
      </div>
      <!-- <div class="m-feed-card__action-item">
          <m-like-action
            :resource-id="feed.resource_id"
            :resource-type="feed.resource_type"
            :like-status="feed.like_status"
            type="dislike"
          >
            <span class="text">
              {{ pipe.num(feed.like_status.dislike.count) || '不喜欢' }}
            </span>
          </m-like-action>
        </div> -->
      <m-link
        nested
        :resource-id="feed.resource_id"
        :resource-type="feed.resource_type"
        :title="feed.title"
        class="m-feed-card__action-item"
      >
        <m-icon icon="ni ni-comment">
          <span class="text">
            {{ pipe.num(feed.floor_count) || '评论' }}
          </span>
        </m-icon>
      </m-link>
      <div class="m-feed-card__action-item">
        <m-forward-action
          :resource-id="feed.resource_id"
          :resource-type="feed.resource_type"
        >
          <span class="text">转发</span>
        </m-forward-action>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.m-feed-card {
  content-visibility: auto;
  contain-intrinsic-size: 400px;

  &__header {
    display: flex;

    .more-button {
      margin-left: auto;
      font-size: 24px;
    }
  }

  &__content-wrapper {
    padding-left: 0;
    @media screen and (min-width: 992px) {
      padding-left: 48px;
    }
  }

  &__body {
    color: var(--text-secondary);
    font-size: 14px;
  }

  &__h3 {
    font-size: 16px;
    line-height: 20px;
    font-weight: 600;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 12px 0 0 0;
    padding: 0;
  }

  &__gallery {
    margin-top: 12px;
  }

  &__content {
    margin-top: 12px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 4;
    display: -webkit-box;
    -webkit-box-orient: vertical;

    img {
      vertical-align: middle;
    }
  }

  &__forward {
    margin-top: 5px;
    padding: 12px;
    background-color: var(--color-zinc-100);
  }

  &__tags {
    margin-top: 12px;
  }

  &__action {
    display: flex;
    justify-content: space-around;
    padding: 20px 0;
    color: var(--color-gray-400);

    &-item {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    :deep(.n-icon) {
      font-size: 18px;
      margin-right: 8px;
    }

    .text {
      font-size: 12px;
      user-select: none;
    }
  }
}
</style>
